import { Money } from "@redotech/money";
import { useRequiredContext } from "@redotech/react-util/context";
import { useEventListener } from "@redotech/react-util/event";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { StyleShadowRoot } from "@redotech/react-util/style";
import {
  itemInInvalidCollection,
  itemValidForFinalSaleReturns,
  itemValidForProtection,
  itemValidForReturns,
  productMatchesTags,
} from "@redotech/redo-shopify-utils/check-product";
import STYLE_MANAGER from "./style-manager";

import {
  DEFAULT_ICON_BACKGROUND_COLOR,
  DEFAULT_ICON_COLOR,
  DEFAULT_IMAGE_URL,
  defaultToggleTextOptions,
  fillEmptyFieldsWithDefault,
  makeEmptyToggleTexts,
} from "@redotech/redo-model/default-values";
import {
  WidgetAutocheck,
  WidgetConfig,
  WidgetElementType,
  WidgetFees,
} from "@redotech/redo-model/widget";
import { getCalculatedCoveragePrice } from "@redotech/redo-shopify-utils/price-calculations";

import {
  binarySearch,
  getThreshold,
} from "@redotech/redo-shopify-utils/binary-search";
import { getAllReturnEligibleCountries } from "@redotech/redo-shopify-utils/country-check";
import { ModalRootContext } from "@redotech/redo-web/modal-root";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import type {
  ShopifyCart,
  ShopifyProduct,
  ShopifyProducts,
  ShopifyVariantJS,
} from "@redotech/shopify-client/ajax";
import {
  ExtensionShopifyClient,
  ShopifyAjaxClient,
  ShopifyCartItem,
  ShopifyClientType,
  ShopifyProductJS,
} from "@redotech/shopify-client/ajax";
import { nativeRandom, randomHexString } from "@redotech/util/random";
import { ReactNode, memo, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { useDebounce } from "usehooks-ts";
import { RedoToggleElement } from "./cart-app";
import { RedoCheckbox } from "./checkbox";
import type { RedoCheckboxElement } from "./checkbox-app";
import { ShopifyExtensionClientContext } from "./client/client-context";
import {
  ShopifyExtensionClient,
  getCollections,
  getSellingPlan,
} from "./client/shopify-extension-client";
import { useLocationManager } from "./location";
import { TAPCART_SELECTOR, addCheckbox } from "./product-page";
import { RedoRoot } from "./root";
import {
  DEFAULT_CART_COUNT_SELECTOR,
  DEFAULT_CHECKOUT_BUTTONS_SELECTOR,
  DEFAULT_PRODUCT_SELECTOR,
  DEFAULT_TOGGLE_CLICK_SELECTOR,
  DEFAULT_TOGGLE_SELECTOR,
} from "./selectors";
import {
  ExtensionShopifyClientContext,
  isOnProductPage,
  shopifyProductHandle,
} from "./shopify";
import { DeluxeToggleArray, RedoToggle } from "./toggle";

import { Currency, isCurrency } from "@redotech/money/currencies";
import { StorefrontCheckoutTextUtils } from "@redotech/redo-model/storefront-checkout-text-utils";
import {
  AttachmentStrategy,
  ToggleFields,
  isAttachmentStrategyCheckoutExtensionEnabled,
  isAttachmentStrategyUsingCheckoutButtons,
} from "@redotech/redo-model/team";
import { CartCard } from "./cart-card";
import { CheckoutButtons } from "./checkout-buttons";
import { StorefrontClient } from "./client/storefront";
import { log } from "./monitoring/log";
import { renderCart } from "./render-cart";
import { FlexibleCheckoutButtons } from "./render-elements/flexible-coverage-checkout-buttons";
import { InfoCard } from "./render-elements/info-card";
import {
  REDO_DISABLE_TAGS,
  getCartShadowRoot,
  getMatchingRedoSellingPlanId,
  isPackageProtection,
  isRedoItem,
  queryDocumentAndShadowRoot,
} from "./utils";
import { useCart } from "./utils/cart";
import { hasValidButtonSelectors } from "./utils/checkout-button-utils";
import {
  getPlacedRedoToggle,
  getReactElementFromPlacement,
  widgetSortOrder,
} from "./utils/element-placement";
import { usePageChange } from "./utils/page-change";

import { CartProductType } from "@redotech/redo-model/cart-product-types";
import { parseGid } from "@redotech/redo-model/shopify/gid-utils";
import { assertNever } from "@redotech/util/type";
import { BigcommerceServerApiClient } from "./client/bigcommerce-server-api-client";
import { BigcommerceStorefrontClient } from "./client/bigcommerce-storefront";
import { getCheckoutOnClickEvent } from "./utils/checkout-buttons/on-click-logic";
import { useBFCacheListener } from "./utils/checkout-buttons/on-page-refresh-hooks";
import { isRIC } from "./utils/ric-check";
import { EnsurePromiseAction } from "./utils/signals";

export function initialize(
  shopifyClient: ExtensionShopifyClient,
  storefrontClient: StorefrontClient | undefined,
  autocheck: WidgetAutocheck,
  cartEnabled: boolean,
  fees: WidgetFees,
  shopifyProducts: ShopifyProducts,
) {
  if (document.querySelector("#redo-container") || window.blockRedo) {
    return;
  }
  RedoToggleElement.register();
  const redoContainer = document.createElement("div");
  redoContainer.id = "redo-container";
  document.head.appendChild(redoContainer);
  const root = createRoot(redoContainer);
  root.render(
    <RedoRoot
      extensionShopifyClient={shopifyClient}
      storeUrl={
        window.redoStorefront?.storeUrl ??
        window.Shopify?.shop ??
        window.Shopify?.Checkout?.apiHost
      }
      widgetId={window.redoWidgetId}
    >
      <RedoManager
        autocheck={autocheck}
        cartEnabled={cartEnabled}
        fees={fees}
        shopifyProducts={shopifyProducts}
        storefrontClient={storefrontClient}
      />
    </RedoRoot>,
  );

  window.removeRedo = () => {
    log("Removing redo");
    root.unmount();
    redoContainer.remove();
  };
}

type SELECTOR_PLACEMENT = "before" | "after" | "prepend" | "append";
const DEFAULT_TOGGLE_PLACEMENT: SELECTOR_PLACEMENT = "before";

const RETURN_TOGGLE_STATE_KEY = "return-toggle-state";
const PROTECTION_TOGGLE_STATE_KEY = "protection-toggle-state";
const SINGLE_TOGGLE_STATE_KEY = "single-toggle-state";
const SELLING_PLANS_ID = "redo-selling-plans";
const REDO_REMOVED_REDO = "redo-remove-redo";

let submitButtonSelector = [
  ".redo-submit-button",
  ".picky-add-selected-to-cart-button", // PickyStory bundles
  'form[action="/cart/add"] [type=submit]',
  ".ajax-submit",
  ".js-add-to-cart",
  ".add-to-cart-btn",
  ".product-buy-buttons--cta",
  ".ProductForm__AddToCart", // pcheebum
].join(", ");

const DEFAULT_CART_COUNT_MODIFICATION = {
  selector: DEFAULT_CART_COUNT_SELECTOR,
  search: "\\d+",
  replace: "%count%",
};
interface RedoManagerProps {
  autocheck: WidgetAutocheck;
  cartEnabled: boolean;
  fees: WidgetFees;
  shopifyProducts: ShopifyProducts;
  storefrontClient?: StorefrontClient;
}

const RedoManager = memo(function RedoManager({
  autocheck,
  cartEnabled,
  fees,
  shopifyProducts,
  storefrontClient,
}: RedoManagerProps) {
  useEffect(() => {
    log("RedoManager mounted");
    return () => {
      log("RedoManager unmounted");
    };
  }, []);

  const locationManager = useLocationManager();
  const shopifyClient = useRequiredContext(ExtensionShopifyClientContext);
  const onTapcart = shopifyClient.type === ShopifyClientType.TAPCART;
  const redoShopifyExtensionClient = useRequiredContext(
    ShopifyExtensionClientContext,
  );
  const merchantPaid = autocheck.paidModel === "merchant_paid";

  const [toggles, setToggles] = useState<
    { redoToggleElements: RedoToggleElement[]; widgetConfig?: WidgetConfig }[]
  >([]);
  const [foundValidButtonSelectors, setFoundValidButtonSelectors] = useState<
    boolean | undefined
  >(undefined);

  useEffect(() => {
    if (!fees || !autocheck.hideRedoProduct?.enabled) {
      return;
    }

    if (
      isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy] &&
      foundValidButtonSelectors === false
    ) {
      return;
    }
    const products = [
      fees.packageProduct,
      fees.returnProduct,
      fees.bothProduct,
    ];

    let productSelector =
      autocheck.hideRedoProduct.productSelector || DEFAULT_PRODUCT_SELECTOR;

    if (isRIC(autocheck)) {
      productSelector += `, ${autocheck.hideRedoProduct.checkoutButtonSelector || DEFAULT_CHECKOUT_BUTTONS_SELECTOR}`;
    }

    const selectors: string[] = [];
    products.forEach((product) => {
      if (!product) {
        return;
      }
      const variants =
        product.variants.length >= 1 ? product.variants : [{ variant_id: "" }];
      variants.forEach((variant) => {
        selectors.push(
          productSelector
            .replaceAll("%handle%", product.handle || "")
            ?.replaceAll(`%product_id%`, String(product.productId) || "")
            ?.replaceAll(
              "%variant_id%",
              product.variants?.length >= 1
                ? parseGid(String(variant.variant_id))
                : "",
            ),
        );
      });
    });
    const selector = selectors.filter(Boolean).join(", ");
    if (!selector) {
      return;
    }
    const style = document.createElement("style");
    style.innerText = `${selector} {display: none !important;}`;
    document.head.appendChild(style);

    // If the cart is inside a shadow root, append the styles there
    const shadowRoot = getCartShadowRoot(autocheck);

    let shadowStyle: HTMLStyleElement | undefined = undefined;
    if (shadowRoot) {
      shadowStyle = document.createElement("style");
      shadowStyle.innerText = `${selector} {display: none !important;}`;
      shadowRoot.appendChild(shadowStyle);
    }

    return () => {
      style.remove();
      shadowStyle?.remove();
    };
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fees, autocheck.hideRedoProduct?.enabled, foundValidButtonSelectors]);

  // Initialize selling plan for redo state.
  useEffect(() => {
    if (!getLocalStorageWithExpiry(SELLING_PLANS_ID)) {
      setLocalStorageWithExpiry(SELLING_PLANS_ID, {});
    }
  }, []);

  const countryLoad = useLoad(
    async (signal) => {
      if (
        !autocheck.countries?.length &&
        !autocheck.countriesPackageProtection?.length
      ) {
        return null;
      }
      const result =
        (!autocheck.forceIPAddressLocation && globalThis.Shopify?.country) ||
        (await locationManager.getCountry(signal));
      /*
        const predictedCountry = window.Shopify?.country;
        if (
          result.trim().toLowerCase() !== predictedCountry?.trim().toLowerCase()
        ) {
          postLocationDiscrepancy(
            widgetClient,
            { signal },
            result,
            predictedCountry ?? "<empty prediction>",
          );
        }
        */
      return result;
    },
    [autocheck, locationManager],
  );

  const returnCountries = getAllReturnEligibleCountries(
    autocheck.countries,
    autocheck.internationalReturnsPricing,
  );
  const validCountry =
    countryLoad.value === null ||
    returnCountries.includes(countryLoad.value!) ||
    returnCountries.length == 0;

  const validFinalSaleCountry =
    countryLoad.value === null ||
    autocheck.countriesFinalSaleReturns?.includes(countryLoad.value!) ||
    autocheck.countriesFinalSaleReturns?.length === 0;

  const validCountryPackageProtection =
    countryLoad.value === null ||
    autocheck.countriesPackageProtection.includes(countryLoad.value!) ||
    autocheck.countriesPackageProtection.length === 0;

  const redoLock = useRef(false);
  const checkboxState = useRef(autocheck.redoAutoCheck);
  const [checkedCart, setCheckedCart] = useState<ShopifyCart | undefined>(
    undefined,
  );
  const [redoInCart, setInCart] = useState<boolean | undefined>(undefined);
  const [singleToggleState, setSingleToggleVal] = useState(false);
  const [returnToggleState, setReturnToggleVal] = useState(false);
  const [protectionToggleState, setProtectionToggleVal] = useState(false);
  const [cartEligibleReturns, setCartEligibleReturns] = useState(false);
  const [cartEligibleFinalSaleReturns, setCartEligibleFinalSaleReturns] =
    useState(false);
  const [cartEligibleProtection, setCartEligibleProtection] = useState(false);
  const [numValidReturnProducts, setNumValidReturnProducts] = useState(0);
  const [cartValueReturnableItems, setCartValueReturnableItems] = useState(0);
  const [cartValuePackageProtectionItems, setCartValuePackageProtectionItems] =
    useState(0);
  const [finalSaleReturnsCartValue, setFinalSaleReturnsCartValue] = useState(0);
  const [onlyFinalSaleReturns, setOnlyFinalSaleReturns] = useState(false);
  const [checkbox, setCheckbox] = useState<RedoCheckboxElement | null>(null);
  const [cartCurrency, setCartCurrency] = useState<Currency>(Currency.USD);
  const [cartRefreshSignal, setCartRefresh] = useState(Symbol());
  const [coverageCheckoutButtonClicked, setCoverageCheckoutButtonClicked] =
    useState<boolean>(false);
  const [toggleRefreshSignal, setToggleRefresh] = useState(Symbol());
  const [checkboxRefresh, setCheckboxRefresh] = useState(Symbol());
  const cartRefresh = useDebounce(cartRefreshSignal, 500);
  const toggleRefresh = useDebounce(toggleRefreshSignal, 500);
  const cart = useCart(cartRefresh);
  const cartItems = cart?.items;
  const cartChecked = !!cart && cart === checkedCart;
  const cartCheckedOnce = !!checkedCart;

  useBFCacheListener(setCoverageCheckoutButtonClicked);

  useEffect(() => {
    if (!autocheck.attachmentStrategy) {
      return;
    }

    if (
      !isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy]
    ) {
      return;
    }
    const valid = hasValidButtonSelectors(toggles);
    if (foundValidButtonSelectors !== valid) {
      setFoundValidButtonSelectors(valid);
    }
  }, [toggles, autocheck.attachmentStrategy, foundValidButtonSelectors]);

  const checkboxLabelOnly =
    cartEnabled ||
    autocheck.checkoutExtensionEnabled ||
    isAttachmentStrategyCheckoutExtensionEnabled[
      autocheck.attachmentStrategy
    ] ||
    merchantPaid;
  const hasTwoOptions =
    autocheck.attachmentStrategy === AttachmentStrategy.DOUBLE_TOGGLE ||
    autocheck.attachmentStrategy === AttachmentStrategy.DROP_DOWN;

  const invalidReturnProductTags = useMemo(
    () =>
      [...(autocheck.finalSaleTags || []), ...REDO_DISABLE_TAGS].map((s) =>
        s.toLowerCase(),
      ),
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const invalidProtectionProductTags = useMemo(
    () =>
      autocheck.protectionExcludeTags
        ? autocheck.protectionExcludeTags.map((s) => s.toLowerCase())
        : [],
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const mainRedoProduct = shopifyProducts.mainShopifyProduct;
  const returnRedoProduct = shopifyProducts.returnShopifyProduct;
  const packageRedoProduct = shopifyProducts.packageShopifyProduct;
  const bothRedoProduct = shopifyProducts.bothShopifyProduct;

  const productTypeMap = new Map<number, string>();

  if (mainRedoProduct && fees) {
    const productTypes = [
      { product: mainRedoProduct, type: fees.mainType },
      { product: returnRedoProduct, type: CartProductType.Return },
      { product: packageRedoProduct, type: CartProductType.PackageProtection },
      { product: bothRedoProduct, type: CartProductType.Both },
    ];
    for (const p of productTypes) {
      if (p.product) productTypeMap.set(p.product.id, p.type);
    }
  }

  function getCorrectlyPricedVariant(
    cartItems: ShopifyCartItem[] | undefined,
    product: ShopifyProductJS,
    productType: string | undefined,
  ): ShopifyVariantJS {
    if (product.variants.length === 1 && product.variants[0] !== undefined) {
      return product.variants[0];
    }

    let variants: ShopifyVariantJS[] = [];

    if (productType === CartProductType.Return) {
      variants = product.variants;
    } else if (productType === CartProductType.Both) {
      variants = product.variants.slice(1);
    } else if (productType === CartProductType.PackageProtection) {
      variants = product.variants;
    }

    const calculatedCoveragePrice = getCalculatedCoveragePrice({
      basePrice: product.price_min,
      autocheck,
      onTapcart,
      cartValueReturnableItems,
      cartValuePackageProtectionItems,
      finalSaleReturnsCartValue,
      cartItems,
      productType,
      numValidReturnProducts,
      country: countryLoad.value,
      onlyFinalSaleReturns,
    });

    log("product calculatedCoveragePrice", calculatedCoveragePrice);

    const threshold = getThreshold({
      country: countryLoad.value ?? undefined,
      unit: "cents",
      targetPrice: calculatedCoveragePrice,
    });

    // Binary search for the variant with the price closest to the newCalculatedCoveragePrice.
    return binarySearch(
      variants,
      calculatedCoveragePrice,
      (v) => v.price,
      threshold,
    );
  }

  const determineRedoProductType = useHandler(
    ({
      fees,
      autocheck,
    }: {
      fees: WidgetFees;
      autocheck: WidgetAutocheck;
    }): CartProductType | null => {
      let mainProductType: CartProductType | null = null;

      if (
        fees.splitProducts &&
        autocheck.coverageEnabled &&
        autocheck.packageProtection.coverageEnabled
      ) {
        if (cartEligibleReturns && cartEligibleProtection) {
          mainProductType = CartProductType.Both;
          if (hasTwoOptions) {
            if (returnToggleState && !protectionToggleState) {
              mainProductType = CartProductType.Return;
            } else if (!returnToggleState && protectionToggleState) {
              mainProductType = CartProductType.PackageProtection;
            }
          }
        } else if (cartEligibleProtection) {
          mainProductType = CartProductType.PackageProtection;
        } else {
          mainProductType = CartProductType.Return;
        }
      }

      return mainProductType ?? fees.mainType;
    },
  );

  const determineRedoProduct = useHandler(
    (
      mainRedoProduct: ShopifyProductJS,
      returnRedoProduct: ShopifyProductJS | null | undefined,
      packageRedoProduct: ShopifyProductJS | null | undefined,
      bothRedoProduct: ShopifyProductJS | null | undefined,
      fees: WidgetFees,
      autocheck: WidgetAutocheck,
    ) => {
      if (returnRedoProduct && packageRedoProduct && bothRedoProduct) {
        const mainProductType: CartProductType | null =
          determineRedoProductType({ fees, autocheck });

        switch (mainProductType) {
          case CartProductType.Return:
            return returnRedoProduct;
          case CartProductType.PackageProtection:
            return packageRedoProduct;
          case CartProductType.Both:
            return bothRedoProduct;
          case CartProductType.ExtendedWarranty:
            log("Unsupported product type");
            return mainRedoProduct;
          case null:
            return mainRedoProduct;
          default:
            assertNever(mainProductType);
        }
      }

      return mainRedoProduct;
    },
  );

  const getVariantAndProduct = useHandler(
    (
      cartItems: ShopifyCartItem[] | undefined,
      mainRedoProduct: ShopifyProductJS,
      returnRedoProduct: ShopifyProductJS | null | undefined,
      packageRedoProduct: ShopifyProductJS | null | undefined,
      bothRedoProduct: ShopifyProductJS | null | undefined,
      fees: WidgetFees,
      autocheck: WidgetAutocheck,
    ): { variant: ShopifyVariantJS; product: ShopifyProductJS } => {
      const product = determineRedoProduct(
        mainRedoProduct,
        returnRedoProduct,
        packageRedoProduct,
        bothRedoProduct,
        fees,
        autocheck,
      );

      const variant = getCorrectlyPricedVariant(
        cartItems,
        product,
        productTypeMap.get(product.id),
      );

      return { product, variant };
    },
  );

  // Finds expected variant every time anything changes.
  const {
    variantId: expectedVariantID,
    productId: expectedProductId,
    price: redoPrice,
    calculatedCoveragePrice: redoCalculatedCoveragePrice,
    productType: redoProductType,
    feeAction: ensurePromiseFeeAction,
  } = useMemo(() => {
    const SKIP_ENSURE_PROMISE = {
      variantId: null,
      productId: null,
      price: null,
      calculatedCoveragePrice: null,
      productType: null,
      feeAction: EnsurePromiseAction.IGNORE,
    };
    const REDO_SHOULD_NOT_BE_IN_CART = {
      variantId: 0,
      productId: 0,
      price: 0,
      calculatedCoveragePrice: 0,
      productType: null,
      feeAction: EnsurePromiseAction.REMOVE,
    };

    if (!fees || !mainRedoProduct || !cartChecked) {
      return SKIP_ENSURE_PROMISE;
    }

    if (checkboxLabelOnly || !coverageCheckoutButtonClicked) {
      if (
        fees.splitProducts &&
        autocheck.packageProtection.coverageEnabled &&
        hasTwoOptions
      ) {
        if (!returnToggleState && !protectionToggleState) {
          return REDO_SHOULD_NOT_BE_IN_CART;
        }
      } else if (!singleToggleState) {
        return REDO_SHOULD_NOT_BE_IN_CART;
      }
    }

    const productType =
      determineRedoProductType({ fees, autocheck }) ?? CartProductType.Return;
    let calculatedCoveragePrice: number = 0;

    const { variant, product } = getVariantAndProduct(
      cartItems,
      mainRedoProduct,
      returnRedoProduct,
      packageRedoProduct,
      bothRedoProduct,
      fees,
      autocheck,
    );

    if (productType && product) {
      calculatedCoveragePrice = getCalculatedCoveragePrice({
        basePrice: product.price_min,
        autocheck,
        onTapcart,
        cartValueReturnableItems,
        cartValuePackageProtectionItems,
        finalSaleReturnsCartValue,
        cartItems,
        productType,
        numValidReturnProducts,
        country: countryLoad.value,
        onlyFinalSaleReturns,
      });
    } else {
      log("Could not calculate coverage price for fee");
    }

    const isAnyToggleEnabled =
      singleToggleState || returnToggleState || protectionToggleState;

    const useCustomFeeInsteadOfProduct =
      autocheck.attachmentStrategy ===
      AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE;

    return {
      variantId: useCustomFeeInsteadOfProduct ? 0 : variant.id,
      productId: useCustomFeeInsteadOfProduct ? 0 : product.id,
      price: useCustomFeeInsteadOfProduct
        ? 0
        : onTapcart
          ? variant.price
          : variant.price / 100, // PRICE HERE!
      calculatedCoveragePrice,
      productType,
      feeAction:
        useCustomFeeInsteadOfProduct && isAnyToggleEnabled
          ? EnsurePromiseAction.ADD
          : EnsurePromiseAction.REMOVE,
    };
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fees,
    mainRedoProduct,
    returnRedoProduct,
    packageRedoProduct,
    bothRedoProduct,
    cartItems,
    redoInCart,
    cartChecked,
    cartEligibleReturns,
    cartEligibleProtection,
    returnToggleState,
    protectionToggleState,
    singleToggleState,
    numValidReturnProducts,
    coverageCheckoutButtonClicked,
  ]);

  // Finds and stores all places toggles should be rendered on the page
  const renderToggles = useHandler(() => {
    if (!cartEnabled || merchantPaid) {
      return;
    }
    log("Rendering toggles");
    let toggleLocations: {
      elements: Element[];
      widgetConfig?: WidgetConfig;
    }[] = [];
    if (
      isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy] &&
      autocheck.widgetConfigs?.length &&
      !onTapcart
    ) {
      for (const widgetConfig of autocheck.widgetConfigs) {
        toggleLocations.push({
          elements: getReactElementFromPlacement({ widgetConfig, autocheck }),
          widgetConfig,
        });
      }
    } else if (autocheck.cartToggle.cartToggleShadowRootSelector) {
      const shadowRoot = getCartShadowRoot(autocheck);
      let shadowRootLocations: Element[] = [];
      shadowRootLocations = shadowRoot
        ? [
            ...Array.from(
              shadowRoot.querySelectorAll(
                onTapcart
                  ? TAPCART_SELECTOR
                  : autocheck.cartToggle.cartToggleShadowRootSelector ||
                      DEFAULT_TOGGLE_SELECTOR,
              ),
            ),
          ]
        : [];

      toggleLocations = [
        {
          elements: [
            ...Array.from(
              document.querySelectorAll(
                onTapcart
                  ? TAPCART_SELECTOR
                  : autocheck.cartToggle.cartToggleSelector ||
                      DEFAULT_TOGGLE_SELECTOR,
              ),
            ),
            ...shadowRootLocations,
          ],
        },
      ];
    } else {
      const docOrShadowRoot = getCartShadowRoot(autocheck) ?? document;
      toggleLocations = [
        {
          elements: [
            ...Array.from(
              docOrShadowRoot.querySelectorAll(
                onTapcart
                  ? TAPCART_SELECTOR
                  : autocheck.cartToggle.cartToggleSelector ||
                      DEFAULT_TOGGLE_SELECTOR,
              ),
            ),
          ],
        },
      ];
    }

    const toggles: {
      redoToggleElements: RedoToggleElement[];
      widgetConfig?: WidgetConfig;
    }[] = [];

    const groupElementTogether =
      autocheck.groupNearbyToggleElementsTogether ?? true;
    if (!groupElementTogether) {
      // sort the elements so they render in the correct order
      toggleLocations.sort(
        (a, b) =>
          widgetSortOrder(a.widgetConfig) - widgetSortOrder(b.widgetConfig),
      );
    }

    for (const { elements, widgetConfig } of toggleLocations) {
      log("Rendering location.");
      toggles.push({
        redoToggleElements: [
          ...elements.map((container): RedoToggleElement => {
            let toggle: RedoToggleElement | undefined;
            switch (
              widgetConfig?.insertionPosition ||
              autocheck.cartToggle.cartTogglePlacement ||
              DEFAULT_TOGGLE_PLACEMENT
            ) {
              case "before":
              case "after":
                toggle = getPlacedRedoToggle({
                  container: container.parentElement,
                  widgetConfig,
                  anyRedoToggle: groupElementTogether,
                });
                if (toggle) {
                  return toggle;
                }
                break;
              case "prepend":
              case "append":
                toggle = getPlacedRedoToggle({
                  container,
                  widgetConfig,
                  anyRedoToggle: groupElementTogether,
                });
                if (toggle) {
                  return toggle;
                }
                break;
            }
            toggle = document.createElement(
              RedoToggleElement.TAG,
            ) as RedoToggleElement;
            toggle.setToggleClass(widgetConfig?.elementType);
            toggle.id = `redo-toggle-${randomHexString(nativeRandom, 8)}`;
            if (
              widgetConfig?.elementType ===
                WidgetElementType.COVERAGE_CHECKOUT_BUTTON ||
              widgetConfig?.elementType ===
                WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON
            ) {
              toggle.useShadowRoot = false;
            }
            // Split selectors by comma and check if any of them match the container
            const selectors = onTapcart
              ? TAPCART_SELECTOR
              : widgetConfig?.placementSelector ||
                autocheck.cartToggle.cartToggleSelector ||
                DEFAULT_TOGGLE_SELECTOR;
            const matchingSelector = selectors
              ?.split(",")
              .find((selector) => container.matches(selector));
            toggle.setAttribute("found-selector", matchingSelector || "");

            const onDisconnect = () => {
              log("toggle disconnected");
              toggle.removeEventListener("disconnect", onDisconnect);
              setTimeout(renderToggles, 1000);
            };
            toggle.addEventListener("disconnect", onDisconnect);

            switch (
              widgetConfig?.insertionPosition ||
              autocheck.cartToggle.cartTogglePlacement ||
              DEFAULT_TOGGLE_PLACEMENT
            ) {
              case "before":
                container.insertAdjacentElement("beforebegin", toggle);
                break;
              case "after":
                container.insertAdjacentElement("afterend", toggle);
                break;
              case "prepend":
                container.insertAdjacentElement("afterbegin", toggle);
                break;
              case "append":
                container.insertAdjacentElement("beforeend", toggle);
                break;
            }
            return toggle;
          }),
        ],
        widgetConfig,
      });
    }

    setToggles(toggles);
  });

  // attach mutation observer to rerender checkout buttons on dom change
  useEffect(() => {
    if (!autocheck.attachmentStrategy) {
      return;
    }

    log("Create mutation observer");
    const mutationObserver = new MutationObserver((event) => {
      if (autocheck.noDebounceButtonRendering) {
        if (cartEligibleReturns || cartEligibleProtection) {
          renderToggles();
        }
      } else {
        setToggleRefresh(Symbol());
      }
    });
    if (
      isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy] ||
      window.redoStorefront?.type == "bigcommerce"
    ) {
      mutationObserver.observe(document, { childList: true, subtree: true });
    } else {
      mutationObserver.disconnect();
    }

    return () => {
      mutationObserver.disconnect();
    };
  }, [
    autocheck.attachmentStrategy,
    autocheck.noDebounceButtonRendering,
    renderToggles,
    cartEligibleReturns,
    cartEligibleProtection,
  ]);

  useEventListener(document, "redo:cart-reload", () => {
    setCartRefresh(Symbol());
  });

  const addRedo = useHandler(async (event?: Event, force = false) => {
    if (
      merchantPaid ||
      (redoInCart && !force) ||
      redoLock.current === true ||
      !expectedVariantID ||
      autocheck.attachmentStrategy ===
        AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE
    ) {
      return;
    }
    // If redo is in the cart and they are eligible for final sale, then they are paying for final sale.
    // FIXME: This will become a problem when we are splitting the products since finalSaleValue may be greater than 0 and they are not buying final sale coverage.
    const hasFinalSaleCoverage =
      finalSaleReturnsCartValue > 0 && cartEligibleFinalSaleReturns;
    try {
      redoLock.current = true;
      setInCart(true);
      const cartParams = new URLSearchParams();
      if (!onTapcart) {
        switch (window?.Shopify?.shop) {
          case "birdiethreads.myshopify.com":
            cartParams.append("ref_icart", "true");
            break;
          case "thedressoutlet.myshopify.com":
            cartParams.append("sections", "cart-drawer,cart-icon-bubble");
            break;
        }

        if (
          window?.Shopify?.shop === "the-vintage-gentlemen.myshopify.com" &&
          window.Rebuy
        ) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
          Rebuy.Cart.addItem({
            id: expectedVariantID,
            quantity: 1,
            properties: hasFinalSaleCoverage
              ? { __final_sale_coverage: "true" }
              : {},
          });
          redoLock.current = false;
          return;
        }
      }
      log("adding redo", { expectedVariantID });
      setLocalStorageWithExpiry(REDO_REMOVED_REDO, false);

      event?.preventDefault();
      event?.stopPropagation();

      const itemsToAdd: { items: Partial<ShopifyCartItem>[] } = {
        items: [
          {
            id: expectedVariantID,
            variant_id: expectedVariantID,
            product_id: expectedProductId,
            quantity: 1,
            properties: hasFinalSaleCoverage
              ? { __final_sale_coverage: "true" }
              : {},
          },
        ],
      };

      // Add redo product to bottom of shopify cart if setting is enabled
      if (!onTapcart && autocheck.cartToggle?.cartReplaceItems) {
        await (shopifyClient as ShopifyAjaxClient).cartAddToEnd(
          itemsToAdd,
          cartParams,
        );
      } else {
        await shopifyClient.cartAdd(itemsToAdd, cartParams);
      }

      await renderCart();

      if (!event) return;

      console.debug(`Resubmitting event ${event.type}`);
      if (
        typeof Rebuy !== "undefined" ||
        window?.Shopify?.shop === "megalashacademy.myshopify.com"
      ) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      if (event.target instanceof HTMLElement && event.type === "click") {
        event.target.click();
      } else {
        event.target!.dispatchEvent(new Event(event.type));
      }
    } catch (e) {
      console.error("Error adding redo to the cart", e);
      // Refresh the cart so we know if it's actually in there or not
      setCartRefresh(Symbol());
    } finally {
      redoLock.current = false;
    }
  });

  const onToggleChange = useHandler(
    (value: boolean, manual: boolean = true) => {
      log("toggle changed", { value, manual });
      setSingleToggleVal(value);
      const state = value ? "enabled" : "disabled";
      setLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY, state);
    },
  );

  const onReturnToggleChange = useHandler(
    (value: boolean, manual: boolean = true) => {
      log("return toggle changed", { value, manual });
      setReturnToggleVal(value);
      const state = value ? "enabled" : "disabled";
      setLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY, state);
    },
  );

  const onProtectionToggleChange = useHandler(
    (value: boolean, manual: boolean = true) => {
      log("package protection toggle changed", { value, manual });
      setProtectionToggleVal(value);
      const state = value ? "enabled" : "disabled";
      setLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY, state);
    },
  );

  const onCheckboxChange = useHandler((value: boolean) => {
    log("checkbox changed", { value });
    checkboxState.current = value;
  });

  const onModalAdd = useHandler(async () => {
    if (!redoInCart) {
      await addRedo();
    }
  });

  const ensurePromise = useRef<Promise<any> | null>(null);
  // Makes sure the correct variant of redo is in the cart.
  useLoad(
    async (signal) => {
      while (ensurePromise.current !== null) {
        try {
          await ensurePromise.current;
        } catch (e) {
          // Who cares
        }
      }
      if (
        !cartChecked ||
        expectedVariantID === null ||
        !checkboxLabelOnly ||
        coverageCheckoutButtonClicked
      ) {
        return;
      }

      let cartChanged = false;
      ensurePromise.current = (async () => {
        log("Ensuring redo", { expectedVariantID });

        if (
          autocheck.attachmentStrategy ===
            AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE &&
          ensurePromiseFeeAction !== EnsurePromiseAction.IGNORE
        ) {
          if (!autocheck.widgetSlug) {
            log("No widget slug, skipping custom fee attachment");
            return;
          }

          const onBigcommerceCheckout =
            window.location?.pathname?.includes("/checkout");

          if (onBigcommerceCheckout) {
            const bigcommerceServerApiClient = new BigcommerceServerApiClient();
            if (
              ensurePromiseFeeAction === EnsurePromiseAction.ADD &&
              redoCalculatedCoveragePrice &&
              redoCalculatedCoveragePrice !== 0 &&
              redoProductType
            ) {
              const attachResponse =
                await bigcommerceServerApiClient.attachCustomFeeToCart({
                  widgetId: autocheck.widgetSlug,
                  checkoutId: (shopifyClient as BigcommerceStorefrontClient)
                    .cartEntityId as string,
                  feeAmount: redoCalculatedCoveragePrice * 0.01,
                  productType: redoProductType,
                });
              if (attachResponse.feeUpdated) {
                log("Attached custom fee to cart, refreshing page");
                window.location.reload();
              }
            } else if (ensurePromiseFeeAction === EnsurePromiseAction.REMOVE) {
              const deleteResponse =
                await bigcommerceServerApiClient.deleteCustomFeeFromCart({
                  widgetId: autocheck.widgetSlug,
                  checkoutId: (shopifyClient as BigcommerceStorefrontClient)
                    .cartEntityId as string,
                });
              if (deleteResponse.feeUpdated) {
                log("Deleted custom fee from cart, refreshing page");
                window.location.reload();
              }
            }
          } else {
            log(
              `Not on checkout page, skipping custom fee attachment. Location: ${window.location.pathname}, calculatedCoveragePrice: ${redoCalculatedCoveragePrice}`,
            );
          }
        }

        // Block checkout while we're making sure the correct variant is in the cart.
        let checkoutIntercepted = false;
        const checkoutInterceptor = autocheck.checkoutSelector
          ? (event: Event) => {
              if (
                event.target instanceof Element &&
                (event.target.matches(autocheck.checkoutSelector!) ||
                  event.target.closest(autocheck.checkoutSelector!))
              ) {
                event.preventDefault();
                event.stopPropagation();
                checkoutIntercepted = true;
                log("Checkout intercepted");
              }
            }
          : null;

        if (checkoutInterceptor) {
          log("Attaching checkout interceptor");
          // Listen on both the main document and the shadow root (if it exists) just in case
          document.addEventListener("click", checkoutInterceptor, {
            capture: true,
          });
          getCartShadowRoot(autocheck)?.addEventListener(
            "click",
            checkoutInterceptor,
            { capture: true },
          );
        }
        try {
          let foundCorrectVariant = false;

          // Fetching the cart from the API instead of using the useCart react state causes issues because they could be inconsistent.
          const _cart =
            window?.Shopify?.shop === "charming-and-main.myshopify.com"
              ? cart // For Charming and Main, experiment with using the useCart state instead
              : await shopifyClient.cartGet(signal); // For all other shops, continuing fetching from the API

          for (const item of _cart.items) {
            try {
              if (isRedoItem(item)) {
                // Remove any incorrect variants
                if (item.variant_id !== expectedVariantID) {
                  log("removing incorrect variant", { item });
                  cartChanged = true;
                  await shopifyClient.removeItemFromCart(
                    item,
                    item.quantity,
                    signal,
                  );
                  log("incorrect variant removed");
                  if (
                    (getLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY) ===
                      "enabled" ||
                      getLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY) ===
                        "enabled" ||
                      getLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY) ===
                        "enabled") &&
                    expectedVariantID === 0
                  ) {
                    setLocalStorageWithExpiry(REDO_REMOVED_REDO, true);
                  }
                } else {
                  foundCorrectVariant = true;
                  if (item.quantity > 1) {
                    // If there is more than one, make sure to fix that.
                    log("updating variant quantity", { item });
                    cartChanged = true;
                    await shopifyClient.removeItemFromCart(
                      item,
                      item.quantity - 1,
                      signal,
                    );
                  }
                }
              }
            } catch (e) {
              console.error("Error in ensure product", e);
            }
          }
          if (!foundCorrectVariant && expectedVariantID !== 0) {
            cartChanged = true;
            await addRedo(undefined, true);
          }
          // Graviet uses cartAddToEnd, which when combined with their theme and renderCart,
          // causes a race condition where the cart state is not updated properly and all items are removed.
          if (cartChanged && window?.Shopify?.shop !== "graviet.co") {
            await renderCart();
          }
        } finally {
          // FIXME The checkout selector gets removed before the cart can be refreshed, so sometimes it doesn't block the customer from going to checkout before we update.
          if (checkoutInterceptor) {
            log("Removing checkout interceptor");
            document.removeEventListener("click", checkoutInterceptor, {
              capture: true,
            });
            getCartShadowRoot(autocheck)?.removeEventListener(
              "click",
              checkoutInterceptor,
              { capture: true },
            );

            // If a checkout click was intercepted, click the checkout button again
            // FIXME If there are double toggles, it is possible for a customer to turn them both off, click checkout,
            //   and for us to re-click the checkout button while the second toggle update is still processing, which
            //   will take the customer to the checkout page with one Redo product still in the cart.
            if (checkoutIntercepted) {
              // Wait for 500ms for the cart to get re-rendered first
              await new Promise((resolve) => setTimeout(resolve, 500));

              log("Re-clicking checkout button");

              const checkoutElements = queryDocumentAndShadowRoot(
                autocheck.checkoutSelector!,
                autocheck,
              );
              // Just in case there are multiple checkout buttons that match the selector,
              // find the first one that we can click on.
              for (const element of checkoutElements) {
                if (element instanceof HTMLElement) {
                  element.click();
                  break;
                }
              }
            }
          }
        }
      })();

      try {
        document.body.dispatchEvent(new CustomEvent("redo:cart-check:start"));
        return await ensurePromise.current;
      } catch (e) {
        log("Error in ensureRedoLoad", e);
      } finally {
        document.body.dispatchEvent(new CustomEvent("redo:cart-check:end"));
        log("Finished ensuring redo", { expectedVariantID });
        ensurePromise.current = null;
      }
    },
    [
      expectedVariantID,
      cartItems,
      coverageCheckoutButtonClicked,
      redoCalculatedCoveragePrice,
      redoProductType,
    ],
  );

  // Check current cart and render toggles.
  const cartCheckLoad = useLoad(
    async (signal) => {
      if (!fees || !mainRedoProduct || !cart) {
        log("skipping cart check", { fees, mainRedoProduct, cart });
        // Set REDO_REMOVED_REDO to true when cart is empty
        setLocalStorageWithExpiry(REDO_REMOVED_REDO, true);
        return;
      }

      setCartCurrency(isCurrency(cart.currency) ? cart.currency : Currency.USD);
      log("checking cart items", { cart });
      // Check if the cart has actually changed.
      let inCart = !!redoInCart;
      const inCartLastCheck = redoInCart === undefined ? true : !!redoInCart;
      let hasValidReturnProduct = cartEligibleReturns;
      let hasValidProtectionProduct = cartEligibleProtection;
      let hasValidFinalSaleReturnsProduct = false;
      const redoItems: ShopifyCartItem[] = [];
      const hasPackageAndSplit =
        autocheck.packageProtection.coverageEnabled &&
        autocheck.packageProtection.splitProducts;
      // Checks if cart line items have changed or if any selling plans have changed.

      // Set these to false until we actually check the cart.
      inCart = false;
      hasValidReturnProduct = false;
      let hasInvalidReturnProduct = false;
      let numberValidReturnProducts = 0;
      let cartValueReturns = 0;
      let cartValuePackageProtection = 0;
      let finalSaleReturnsCartValue = 0;
      hasValidProtectionProduct = false;
      let hasInvalidProtectionProduct = false;

      if (cart?.items?.length) {
        for (const item of cart.items) {
          if (isRedoItem(item)) {
            inCart = true;
            redoItems.push(item);
            continue;
          }

          // don't skip if we have this setting or if we're using dynamic pricing
          if (
            !autocheck.excludeCoverageIfAnyInvalidProduct &&
            !autocheck.dynamicReturnsPricing
          ) {
            // No need to check product tags if we already have one valid product.
            // Unless we have package protection or final sale coverage enabled
            if (
              ((hasValidReturnProduct && hasValidProtectionProduct) ||
                !item.handle) &&
              !autocheck.packageProtection.coverageEnabled &&
              !autocheck.finalSaleReturns.coverageEnabled
            ) {
              continue;
            }
          }

          // Don't offer coverage for digital products
          if (!item.requires_shipping) {
            continue;
          }

          const itemValidForOfferings = await itemValidForReturnsFrontend({
            autocheck,
            item,
            redoShopifyExtensionClient,
            shopifyClient,
            signal,
            invalidReturnProductTags,
            invalidProtectionProductTags,
          });

          if (itemValidForOfferings.returnsValid) {
            cartValueReturns += item.line_price || 0;
            numberValidReturnProducts += item.quantity;
            hasValidReturnProduct = itemValidForOfferings.returnsValid;
          } else {
            hasInvalidReturnProduct = !itemValidForOfferings.returnsValid;
          }

          if (itemValidForOfferings.protectionValid) {
            cartValuePackageProtection += item.line_price || 0;
            hasValidProtectionProduct = itemValidForOfferings.protectionValid;
          } else {
            hasInvalidProtectionProduct =
              !itemValidForOfferings.protectionValid;
          }

          if (
            itemValidForOfferings.finalSaleReturnsValid &&
            autocheck.finalSaleReturns.coverageEnabled
          ) {
            finalSaleReturnsCartValue += item.line_price || 0;
            hasValidFinalSaleReturnsProduct =
              itemValidForOfferings.finalSaleReturnsValid;
          }
        }

        setOnlyFinalSaleReturns(
          hasValidFinalSaleReturnsProduct && !hasValidReturnProduct,
        );

        const cartTotal = onTapcart
          ? window.Tapcart.variables.cart.subtotal
          : cart.items.reduce(
              (acc, item) => acc + (item.line_price || 0) * item.quantity,
              0,
            ) / 100;

        console.log("cartTotal", cartTotal);
        // Using the same boolean as return products because final sale coverage uses the same product as return coverage
        hasValidReturnProduct =
          hasValidReturnProduct || hasValidFinalSaleReturnsProduct;

        if (
          autocheck.packageProtection.maxCoverageEligibleCartTotal &&
          cartTotal > autocheck.packageProtection.maxCoverageEligibleCartTotal
        ) {
          hasValidProtectionProduct = false;
        }

        if (autocheck.excludeCoverageIfAnyInvalidProduct) {
          hasValidReturnProduct =
            hasValidReturnProduct && !hasInvalidReturnProduct;
          hasValidProtectionProduct =
            hasValidProtectionProduct && !hasInvalidProtectionProduct;
          if (hasInvalidReturnProduct) {
            log(
              "cart ineligible for returns because there's an invalid product in the cart",
              {
                excludeCoverageIfAnyInvalidProduct:
                  autocheck.excludeCoverageIfAnyInvalidProduct,
              },
            );
          }
        }
        setNumValidReturnProducts(numberValidReturnProducts);
        setCartValueReturnableItems(cartValueReturns);
        setCartValuePackageProtectionItems(cartValuePackageProtection);
        setFinalSaleReturnsCartValue(finalSaleReturnsCartValue);
      }
      if (signal.aborted) {
        return;
      }

      log("cart items checked", {
        inCart,
        inCartLastCheck,
        redoItems,
        hasValidReturnProduct,
        hasValidProtectionProduct,
        hasValidFinalSaleReturnsProduct,
      });
      if (!redoItems.length) {
        setLocalStorageWithExpiry(SELLING_PLANS_ID, {});
      }

      if (inCart && redoItems.length) {
        let localStorageSellingPlans: any =
          getLocalStorageWithExpiry(SELLING_PLANS_ID) || {};

        const sellingPlanPromises = cart.items
          .filter((item) => !isRedoItem(item))
          .filter((item) => item.selling_plan_allocation?.selling_plan?.id)
          .map(
            async (item) =>
              await getSellingPlan(redoShopifyExtensionClient, {
                sellingPlanId: item.selling_plan_allocation!.selling_plan!.id,
              }),
          );

        const sellingPlans = await Promise.all(sellingPlanPromises);
        for (const redoItem of redoItems) {
          // find the same item in fees
          const feesItem = [
            fees.returnProduct,
            fees.packageProduct,
            fees.bothProduct,
          ].find((e) => e?.productId === redoItem.product_id);

          if (!feesItem) {
            continue;
          }

          const sellingPlanId = sellingPlans?.length
            ? await getMatchingRedoSellingPlanId(feesItem, sellingPlans)
            : null;

          const redoHasSellingPlan =
            !!redoItem?.selling_plan_allocation?.selling_plan?.id;
          const redoRemovedSellingPlan =
            localStorageSellingPlans[redoItem.product_id] === "removed";

          // If the customer removes the selling plan, we will skip updates
          if (
            sellingPlanId &&
            redoHasSellingPlan &&
            !redoRemovedSellingPlan &&
            localStorageSellingPlans[redoItem.product_id]
          ) {
            continue;
          }
          // If there is a selling plan for the item, add it to redo and add it to local storage
          // If the customer updates their selling plan, we will update the redo item and the local storage
          if (sellingPlanId) {
            localStorageSellingPlans = {
              ...localStorageSellingPlans,
              [redoItem.product_id]: sellingPlanId,
            };

            setLocalStorageWithExpiry(
              SELLING_PLANS_ID,
              localStorageSellingPlans,
            );

            log("updating selling plan", { redoItem, sellingPlanId });
            await shopifyClient.updateCartItemSellingPlan(
              redoItem,
              sellingPlanId,
            );
            // If there is no selling plan for the item but redo has a selling plan, remove it from redo and local storage
          } else if (redoHasSellingPlan) {
            log("removing selling plan", { redoItem });
            await shopifyClient.updateCartItemSellingPlan(redoItem, null);
          }

          if (!sellingPlanId && localStorageSellingPlans[redoItem.product_id]) {
            localStorageSellingPlans = {
              ...localStorageSellingPlans,
              [redoItem.product_id]: "removed",
            };

            setLocalStorageWithExpiry(
              SELLING_PLANS_ID,
              localStorageSellingPlans,
            );
          }
        }
      }
      const returnState =
        getLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY) === "enabled";
      const protectionState =
        getLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY) === "enabled";
      const singleState =
        getLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY) === "enabled";

      let redoForciblyRemoved = false;
      let hasEligibleProduct = false;

      const isZapietPickup = cart.attributes?.["Checkout-Method"] === "pickup";
      const returnsEligibleAndCovered =
        hasValidReturnProduct &&
        autocheck.coverageEnabled &&
        validCountry &&
        !isZapietPickup;
      const packageProtectionEligibleAndCovered =
        autocheck.packageProtection?.coverageEnabled &&
        hasValidProtectionProduct &&
        validCountryPackageProtection &&
        !isZapietPickup;
      const finalSaleReturnsEligibleAndCovered =
        hasValidFinalSaleReturnsProduct &&
        autocheck.finalSaleReturns.coverageEnabled &&
        validFinalSaleCountry &&
        !isZapietPickup;

      if (
        !autocheck.packageProtection?.splitProducts &&
        autocheck.coverageEnabled &&
        autocheck.packageProtection?.coverageEnabled
      ) {
        hasEligibleProduct =
          returnsEligibleAndCovered && packageProtectionEligibleAndCovered;
      } else {
        hasEligibleProduct =
          returnsEligibleAndCovered || packageProtectionEligibleAndCovered;
      }

      // Make sure redo isn't in cart if no eligible products are in cart
      if (!hasEligibleProduct) {
        setSingleToggleVal(false);
        setReturnToggleVal(false);
        setProtectionToggleVal(false);
      }

      const redoWasRemoved = !inCart && hasEligibleProduct && inCartLastCheck;
      const redoWasRemovedByRedo: boolean =
        getLocalStorageWithExpiry(REDO_REMOVED_REDO);
      if (hasPackageAndSplit && hasTwoOptions) {
        if ((returnState || protectionState) && redoWasRemoved) {
          redoForciblyRemoved = true;
        }
      } else if (singleState && redoWasRemoved && !redoWasRemovedByRedo) {
        redoForciblyRemoved = true;
      }

      // If redo was removed by customer set all to false
      if (redoForciblyRemoved && !ensurePromise.current) {
        log("redo was forcibly removed or disabled from cart attribute");
        if (hasPackageAndSplit && hasTwoOptions) {
          if (returnToggleState) {
            setReturnToggleVal(false);
            setLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY, "disabled");
          }
          if (protectionToggleState) {
            setProtectionToggleVal(false);
            setLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY, "disabled");
          }
        } else {
          if (singleToggleState) {
            setSingleToggleVal(false);
            setLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY, "disabled");
          }
        }
      }

      const returnDisabled =
        getLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY) === "disabled";
      const protectionDisabled =
        getLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY) === "disabled";
      const singleDisabled =
        getLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY) === "disabled";

      setCartEligibleReturns(returnsEligibleAndCovered);
      setCartEligibleProtection(packageProtectionEligibleAndCovered);
      setCartEligibleFinalSaleReturns(finalSaleReturnsEligibleAndCovered);

      const bothInCart =
        fees.bothProduct?.productId === redoItems[0]?.product_id;
      const returnInCart =
        bothInCart ||
        fees.returnProduct?.productId === redoItems[0]?.product_id;
      const packageInCart =
        bothInCart ||
        fees.packageProduct?.productId === redoItems[0]?.product_id;

      if (
        cartEnabled &&
        !redoForciblyRemoved &&
        !isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy]
      ) {
        // If cart is eligible for adding a redo item, add it by changing toggles
        if (hasPackageAndSplit && autocheck.coverageEnabled && hasTwoOptions) {
          if (
            hasValidReturnProduct &&
            !returnToggleState &&
            (returnInCart || (autocheck.redoAutoCheck && !returnDisabled))
          ) {
            setReturnToggleVal(true);
            setLocalStorageWithExpiry(RETURN_TOGGLE_STATE_KEY, "enabled");
          }
          if (
            hasValidProtectionProduct &&
            !protectionToggleState &&
            (packageInCart ||
              (autocheck.packageProtectionAutoCheck && !protectionDisabled))
          ) {
            setProtectionToggleVal(true);
            setLocalStorageWithExpiry(PROTECTION_TOGGLE_STATE_KEY, "enabled");
          }
        } else if (
          hasEligibleProduct &&
          !singleToggleState &&
          (inCart || (autocheck.redoAutoCheck && !singleDisabled))
        ) {
          setSingleToggleVal(true);
          setLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY, "enabled");
        }
      }

      setInCart(inCart);
      setCheckedCart(cart);

      if (cart?.items?.length) {
        // Check if any item has coverage
        const hasAnyCoverage = await Promise.any(
          cart.items.map(async (item) => {
            const itemValidForOfferings = await itemValidForReturnsFrontend({
              autocheck,
              item,
              redoShopifyExtensionClient,
              shopifyClient,
              signal,
              invalidReturnProductTags,
              invalidProtectionProductTags,
            });
            return (
              itemValidForOfferings.returnsValid ||
              itemValidForOfferings.protectionValid ||
              itemValidForOfferings.finalSaleReturnsValid
            );
          }),
        );

        // Set REDO_REMOVED_REDO based on coverage status
        setLocalStorageWithExpiry(REDO_REMOVED_REDO, !hasAnyCoverage);
      } else {
        // Set to true if cart is empty
        setLocalStorageWithExpiry(REDO_REMOVED_REDO, true);
      }
    },
    [
      cartItems,
      fees,
      validCountry,
      validCountryPackageProtection,
      mainRedoProduct,
    ],
  );

  useEffect(() => {
    if (cartCheckLoad.error) {
      log("error checking cart", { error: cartCheckLoad.error });
    }
  }, [cartCheckLoad.error]);

  useEffect(() => {
    if (cartEligibleReturns || cartEligibleProtection) {
      log("Eligible for returns or package protection.");
      renderToggles();
    } else {
      log("Not eligible for any product.");
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    cart,
    cartRefresh,
    cartEligibleReturns,
    cartEligibleProtection,
    toggleRefresh,
  ]);

  const getFormatter = (currency: Currency, removeCents: boolean) => {
    return new Intl.NumberFormat("en-US", {
      currency: currency,
      minimumFractionDigits: removeCents ? 0 : 2,
      maximumFractionDigits: removeCents ? 0 : 2,
    });
  };

  // Update the cart counters whenever the cart items change.
  useEffect(() => {
    if (!autocheck.hideRedoProduct?.enabled || !cartItems?.length) {
      return;
    }

    const count = cartItems.reduce((acc, item) => {
      if (isRedoItem(item) || isPackageProtection(item)) {
        return acc;
      }
      return acc + item.quantity;
    }, 0);

    const line_item_count = cartItems.reduce((acc, item) => {
      if (isRedoItem(item) || isPackageProtection(item)) {
        return acc;
      }
      return acc + 1;
    }, 0);

    const cartCountModifications =
      (autocheck.hideRedoProduct?.cartCountModifications?.length ?? 0) > 0
        ? autocheck.hideRedoProduct?.cartCountModifications || [
            DEFAULT_CART_COUNT_MODIFICATION,
          ]
        : [DEFAULT_CART_COUNT_MODIFICATION];

    for (const mod of cartCountModifications) {
      const search = new RegExp(mod.search, "g");
      const replace = mod.replace
        .replaceAll("%count%", count.toString())
        .replaceAll("%line_item_count%", line_item_count.toString());
      const elements = queryDocumentAndShadowRoot(mod.selector, autocheck);
      for (const element of elements) {
        element.innerHTML = element.innerHTML.replace(search, replace);
        // Jack's Surf Shop
        if (element.hasAttribute("data-header-cart-count")) {
          element.setAttribute("data-header-cart-count", count.toString());
        }
      }
    }

    const cart_subtotal =
      cartItems.reduce((acc, item) => {
        if (isRedoItem(item) || isPackageProtection(item)) {
          return acc;
        }
        return acc + (item.line_price || 0);
      }, 0) / 100;
    const redo_subtotal =
      cartItems.reduce((acc, item) => {
        if (isRedoItem(item)) {
          return acc + (item.line_price || 0);
        }
        return acc;
      }, 0) / 100;

    const cartPriceModifications =
      (autocheck.hideRedoProduct?.cartPriceModifications?.length ?? 0) > 0
        ? autocheck.hideRedoProduct?.cartPriceModifications || []
        : [];

    for (const mod of cartPriceModifications) {
      const search = new RegExp(mod.search, "g");
      const removeCents = mod.replace.includes("round");
      const includeRedo = mod.replace.includes("withRedo");
      const replace = mod.replace.replaceAll(
        /%cart-subtotal.*%/g,
        getFormatter(cartCurrency, removeCents).format(
          cart_subtotal + (includeRedo ? redo_subtotal : 0),
        ),
      );
      const elements = queryDocumentAndShadowRoot(mod.selector, autocheck);
      for (const element of elements) {
        element.innerHTML = element.innerHTML.replace(search, replace);
      }
    }

    const cart_discount =
      cartItems.reduce((acc, item) => {
        if (isRedoItem(item) || isPackageProtection(item)) {
          return acc;
        }
        return (
          acc +
          (item.discounts?.reduce((acc2, discount) => {
            return acc2 + discount?.amount || 0;
          }, 0) || 0)
        );
      }, 0) / 100;

    const cartDiscountModifications =
      (autocheck.hideRedoProduct?.cartDiscountModifications?.length ?? 0) > 0
        ? autocheck.hideRedoProduct?.cartDiscountModifications || []
        : [];

    for (const mod of cartDiscountModifications) {
      const search = new RegExp(mod.search, "g");
      const removeCents = mod.replace.includes("round");
      const replace = mod.replace.replaceAll(
        /%cart-discount.*%/g,
        getFormatter(cartCurrency, removeCents).format(cart_discount),
      );
      const elements = queryDocumentAndShadowRoot(mod.selector, autocheck);
      for (const element of elements) {
        element.innerHTML = element.innerHTML.replace(search, replace);
      }
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, cartRefresh]);

  // Listen for click events and refresh toggles.
  useEventListener(
    document,
    "click",
    (e: Event) => {
      if (
        e.target instanceof Element &&
        // Check if the click was one of the elements that could trigger a cart change or open.
        ((autocheck.cartToggle.cartClickListenerSelector &&
          e.target.matches(autocheck.cartToggle.cartClickListenerSelector)) ||
          e.target.matches(DEFAULT_TOGGLE_CLICK_SELECTOR))
      ) {
        for (let i = 1; i <= 5; i *= 2) {
          setTimeout(() => setCartRefresh(Symbol()), i * 500);
        }
      }
    },
    true,
  );

  const pageChange = usePageChange();
  // This will trigger the checkbox to be added if it needs to be, as well as re-rendering the toggle.
  useEffect(() => {
    setCheckboxRefresh(Symbol());
    setCartRefresh(Symbol());
  }, [pageChange]);

  // Add checkbox if on product page
  useEffect(() => {
    if (document.querySelector("redo-checkbox") || isOnProductPage(onTapcart)) {
      if (autocheck.submitButtonSelector) {
        submitButtonSelector += `, ${autocheck.submitButtonSelector}`;
      }
      setCheckbox(addCheckbox(autocheck, onTapcart));
    } else if (checkbox) {
      setCheckbox(null);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkboxRefresh]);

  // what is this?
  const validProductLoad = useLoad(
    async (signal) => {
      if (
        checkbox &&
        (document.querySelector("redo-checkbox") || isOnProductPage(onTapcart))
      ) {
        const handle = onTapcart
          ? window.Tapcart.variables.product.handle
          : shopifyProductHandle(location.href);
        if (!handle) {
          return false;
        }
        const product = await shopifyClient.productGet(handle, signal);

        let collections: { title: string; handle: string }[] = [];

        if (storefrontClient) {
          collections =
            (await storefrontClient.getCollections(product.id.toString())) ??
            [];
        } else {
          collections = await getCollections(redoShopifyExtensionClient, {
            signal,
            productId: String(product.id),
          });
        }

        const excludeByCollection: boolean = itemInInvalidCollection(
          collections.map((collection) => collection.title),
          autocheck.coverageExcludeCollections,
        );

        const productTags = onTapcart
          ? window.Tapcart.variables.product.tags
          : getTags(await shopifyClient.productGet(handle, signal));

        const validForFinalSaleReturns =
          autocheck.finalSaleReturns.coverageEnabled &&
          itemValidForFinalSaleReturns({
            validProductTags: autocheck.finalSaleReturns.coverageValidTags,
            validCollections:
              autocheck.finalSaleReturns.coverageValidCollections,
            itemInfo: {
              productTags,
              collections: collections.map((c) => c.handle),
            },
          });
        return (
          (!productMatchesTags(productTags, invalidReturnProductTags) &&
            !excludeByCollection) ||
          validForFinalSaleReturns
        );
      }
      return false;
    },
    [checkbox],
  );
  useEffect(() => {
    if (validProductLoad.error) {
      console.error(validProductLoad.error);
    }
  }, [validProductLoad.error]);

  const showCheckbox =
    (checkboxLabelOnly || !redoInCart) &&
    validProductLoad.value === true &&
    (cartCheckedOnce || merchantPaid) &&
    validCountry;
  // Add checkbox event handlers
  useEffect(() => {
    if (!checkbox || !showCheckbox || checkboxLabelOnly) {
      return;
    }

    // Boolean to make sure we only add redo once for these handlers.
    // Once we add it, they will all be removed on the next re-render
    // because showCheckbox will be false.
    let added = false;
    const form = checkbox.closest("form");
    async function formEventHandler(e: SubmitEvent) {
      if (checkboxState.current && !added) {
        added = true;
        await addRedo(e);
      }
    }
    async function rebuyHandler(e: any): Promise<void> {
      if (checkboxState.current && !added) {
        added = true;
        // Don't add redo when we're already adding redo.
        if (e?.detail?.item?.variant_id === fees?.variantId) {
          return;
        }
        await addRedo();
      }
    }
    async function clickEventHandler(e: MouseEvent): Promise<void> {
      if (
        e.target instanceof Element &&
        window?.Shopify?.theme?.name === "Dawn" &&
        e.target.closest(".shopify-payment-button__button")
      ) {
        // Buy it now button for the dawn theme
        const quantityElement = document.querySelector(
          '.quantity__input, input[type="number"]',
        ) as HTMLInputElement | null;
        const quantity = quantityElement?.value || "1";
        const currentSelectedVariant = (
          document.querySelector(
            ".product__pickup-availabilities",
          ) as HTMLElement
        ).dataset.variantId;
        e.preventDefault();
        e.stopPropagation();
        window.location.href = `https://${
          window?.Shopify.shop
        }/cart/${currentSelectedVariant}:${quantity},${
          fees!.variantId
        }:1?traffic_source=buy_now`;
      } else if (
        e.target instanceof Element &&
        e.target.closest(submitButtonSelector) &&
        checkboxState.current &&
        !added
      ) {
        added = true;
        await addRedo(e);
      }
    }

    form?.addEventListener("submit", formEventHandler);
    document.addEventListener("rebuy:cart.add", rebuyHandler);
    document.addEventListener("click", clickEventHandler, true);

    return () => {
      form?.removeEventListener("submit", formEventHandler);
      document.removeEventListener("rebuy:cart.add", rebuyHandler);
      document.removeEventListener("click", clickEventHandler);
    };
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkbox, showCheckbox, cartEnabled]);

  if (!fees) {
    return null;
  }

  if (
    !merchantPaid &&
    (autocheck.disableRedoForCustomer ||
      (autocheck.hideCheckbox && !autocheck.cartToggle.enabled && !onTapcart) ||
      !autocheck.enabled ||
      (autocheck.hideCheckbox && !cartCheckedOnce))
  ) {
    log("returning empty", {
      merchantPaid,
      disableRedoForCustomer: autocheck.disableRedoForCustomer,
      hideCheckbox: autocheck.hideCheckbox,
      cartToggleEnabled: autocheck.cartToggle.enabled,
      enabled: autocheck.enabled,
      cartChecked,
    });
    return null;
  }

  const hasProtectionReturnsAndSplit =
    autocheck.packageProtection.coverageEnabled &&
    autocheck.coverageEnabled &&
    autocheck.packageProtection.splitProducts;

  // determine toggle fields

  let toggleText = autocheck.checkboxText;
  const allToggleOptions = makeEmptyToggleTexts();
  const defaults = defaultToggleTextOptions(autocheck.coverage);

  if (autocheck.toggleTextOptions) {
    allToggleOptions.returnToggle = fillEmptyFieldsWithDefault(
      autocheck.toggleTextOptions.returnToggle,
      defaults.returnToggle,
    );
    allToggleOptions.packageProtectionToggle = fillEmptyFieldsWithDefault(
      autocheck.toggleTextOptions.packageProtectionToggle,
      defaults.packageProtectionToggle,
    );
    allToggleOptions.bothProductToggle = fillEmptyFieldsWithDefault(
      autocheck.toggleTextOptions.bothProductToggle,
      defaults.bothProductToggle,
    );
  }

  let singleToggleFields: ToggleFields | undefined =
    allToggleOptions.returnToggle;
  if (fees.mainType === CartProductType.Return) {
    singleToggleFields = allToggleOptions.returnToggle;
  } else if (fees.mainType === CartProductType.Both) {
    singleToggleFields = allToggleOptions.bothProductToggle;
  } else if (fees.mainType === CartProductType.PackageProtection) {
    singleToggleFields = allToggleOptions.packageProtectionToggle;
  }

  if (hasProtectionReturnsAndSplit) {
    if (cartEligibleReturns && cartEligibleProtection) {
      singleToggleFields = allToggleOptions.bothProductToggle;
    } else if (cartEligibleProtection) {
      singleToggleFields = allToggleOptions.packageProtectionToggle;
    } else {
      singleToggleFields = allToggleOptions.packageProtectionToggle;
    }
  }

  if (
    !autocheck.checkboxText &&
    !(
      singleToggleFields.titleText === defaults.bothProductToggle.titleText ||
      singleToggleFields.titleText === defaults.returnToggle.titleText ||
      singleToggleFields.titleText ===
        defaults.packageProtectionToggle.titleText
    )
  ) {
    toggleText = singleToggleFields.titleText;
  }

  if (fees.splitProducts || autocheck.packageProtection.coverageEnabled) {
    toggleText = singleToggleFields.titleText;
  }

  if (autocheck.offerDiscountOnNextOrder) {
    const cartValue = onTapcart
      ? window.Tapcart.variables.cart.subtotal
      : cartItems?.reduce((acc, item) => acc + (item.line_price || 0), 0) || 0;

    if (cartValue !== 0) {
      const match = extractFlatValue(singleToggleFields.titleText);
      if (match != null && cartValue / 100 >= match.cartValue) {
        singleToggleFields.titleText = singleToggleFields.titleText.replace(
          match.replaceString,
          String(match.storeCredit),
        );
      } else if (match != null) {
        singleToggleFields.titleText = "%%HIDEME%%"; // HACK: for lime ricki's AB test, which needed to launch today using behavior we don't support
      }
    }

    const storeCredit = (
      ((Number(autocheck.offerDiscountOnNextOrderPercentage) / 100) *
        cartValue) /
      100
    ) // it's returned as cents so we divide by 100 again...
      .toFixed(2);

    // something asynchronous is happening. If the cart subtotal hasn't loaded then storeCredit is 0.00 which we do not want.
    if (!(storeCredit === "0.00")) {
      if (!toggleText) {
        toggleText = "Get $%value% off your next order";
      }
      toggleText = toggleText.replace(/%value%/g, storeCredit);
      singleToggleFields.titleText = singleToggleFields.titleText.replace(
        /%value%/g,
        storeCredit,
      );
      singleToggleFields.positiveSubtext =
        singleToggleFields.positiveSubtext.replace(/%value%/g, storeCredit);
      singleToggleFields.negativeSubtext =
        singleToggleFields.negativeSubtext.replace(/%value%/g, storeCredit);
    }
  }

  const image_url = autocheck.logo || DEFAULT_IMAGE_URL;
  const iconColor = autocheck.iconColor || DEFAULT_ICON_COLOR;
  const iconBackgroundColor =
    autocheck.iconBackgroundColor || DEFAULT_ICON_BACKGROUND_COLOR;
  const priceDivisor = onTapcart ? 1 : 100;

  let showToggles = false;
  const hasBothCoverage =
    autocheck.coverageEnabled && autocheck.packageProtection?.coverageEnabled;

  if (hasBothCoverage && !autocheck.packageProtection?.splitProducts) {
    showToggles = cartEligibleReturns && cartEligibleProtection;
  } else {
    showToggles = cartEligibleReturns || cartEligibleProtection;
  }

  if (!showToggles) {
    log({ showToggles }, "cart is not eligible");
  }

  if (
    isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy] &&
    autocheck.hideRedoProduct
  ) {
    autocheck.hideRedoProduct.enabled = showToggles;
  }

  let cartReactElement: ReactNode = null;
  const cartReactElements: JSX.Element[] = [];

  let singleProductPrice;

  if (showToggles && mainRedoProduct) {
    const singleProductType = determineRedoProductType({ fees, autocheck });
    const singleProduct = determineRedoProduct(
      mainRedoProduct,
      returnRedoProduct,
      packageRedoProduct,
      bothRedoProduct,
      fees,
      autocheck,
    );
    const singleProductVariant = getCorrectlyPricedVariant(
      cartItems,
      singleProduct,
      productTypeMap.get(singleProduct.id),
    );
    if (
      autocheck.attachmentStrategy ===
      AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE
    ) {
      const calculatedCoveragePriceToEnable = getCalculatedCoveragePrice({
        basePrice: singleProduct.price_min,
        autocheck,
        onTapcart,
        cartValueReturnableItems,
        cartValuePackageProtectionItems,
        finalSaleReturnsCartValue,
        cartItems,
        productType: singleProductType ?? CartProductType.Return,
        numValidReturnProducts,
        country: countryLoad.value,
        onlyFinalSaleReturns,
      });
      singleProductPrice =
        (calculatedCoveragePriceToEnable ?? 0) / priceDivisor;
    } else {
      singleProductPrice = singleProductVariant.price / priceDivisor;
    }

    const cart_subtotal =
      (cartItems?.reduce((acc, item) => {
        if (isRedoItem(item) || isPackageProtection(item)) {
          return acc;
        }
        return acc + (item.line_price || 0);
      }, 0) || 0) / 100;

    const pre_discount_cart_subtotal =
      (cartItems?.reduce((acc, item) => {
        if (isRedoItem(item) || isPackageProtection(item)) {
          return acc;
        }
        return acc + (item.original_line_price || 0);
      }, 0) || 0) / 100;

    const hasFinalSaleCoverage =
      finalSaleReturnsCartValue > 0 && cartEligibleFinalSaleReturns;

    const cartParams = new URLSearchParams();
    const itemsToAdd: { items: Partial<ShopifyCartItem>[] } = {
      items: [
        {
          id: singleProductVariant.id,
          quantity: 1,
          properties: hasFinalSaleCoverage
            ? { __final_sale_coverage: "true" }
            : {},
        },
      ],
    };

    const client = storefrontClient ?? shopifyClient;

    const onClickProps = {
      autocheck,
      client,
      itemsToAdd,
      cartParams,
      coverageCheckoutButtonClicked,
      setCoverageCheckoutButtonClicked,
    };
    const onClickCoverage = getCheckoutOnClickEvent(true, onClickProps);
    const onClickNoCoverage = getCheckoutOnClickEvent(false, onClickProps);

    const textInfo = {
      fees,
      hasProtectionReturnsAndSplit,
      cartEligibleProtection,
      cartEligibleReturns,
      currency: cartCurrency,
      coveragePrice: singleProductPrice,
      cartTotal: cart_subtotal,
      cartTotalPreDiscount: pre_discount_cart_subtotal,
    };

    const defaultCoverageButtonTexts =
      StorefrontCheckoutTextUtils.getCoverageTextAndSubtext(
        StorefrontCheckoutTextUtils.defaultCheckoutTexts.coverageButtonText,
        textInfo,
      );

    const defaultTextsForInfoCard =
      StorefrontCheckoutTextUtils.getCoverageTextAndSubtext(
        StorefrontCheckoutTextUtils.defaultCheckoutTexts.cardText,
        textInfo,
      );

    const defaultTextsForNonCoverageButton =
      StorefrontCheckoutTextUtils.getTextByProduct(
        StorefrontCheckoutTextUtils.defaultCheckoutTexts.nonCoverageButtonText,
        textInfo,
      );

    if (
      autocheck.attachmentStrategy === AttachmentStrategy.SINGLE_TOGGLE ||
      autocheck.attachmentStrategy ===
        AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE ||
      (!autocheck.attachmentStrategy && autocheck.cartToggle.enabled === true)
    ) {
      const deluxeVar = autocheck.toggleSubtextEnabled ?? false;
      const toggle = (
        <RedoToggle
          color={autocheck.textColor}
          coverage={autocheck.coverage}
          customCss={autocheck.customToggleCss}
          fontSize={autocheck.toggleFontSize}
          html={autocheck.html}
          isDelux={deluxeVar}
          modalCustomizationsV2={autocheck.infoModal}
          modalVersion={autocheck.infoModal?.version}
          onChange={onToggleChange}
          packageProtectionEnabled={autocheck.packageProtection.coverageEnabled}
          packageProtectionPlusEnabled={
            autocheck.packageProtection.packageProtectionPlusEnabled
          }
          paymentModel={autocheck.paidModel}
          price={new Money(singleProductPrice || +fees.price, cartCurrency)}
          showInfoIcon={autocheck.infoModal?.cartIconEnabled}
          toggleCircleColor={autocheck.cartToggle.toggleCircleColor}
          toggleFields={singleToggleFields}
          toggleOffColor={autocheck.cartToggle.toggleOffColor}
          toggleOnColor={autocheck.cartToggle.toggleOnColor}
          toggleText={toggleText}
          toggleTextAfterRedo={autocheck.checkboxTextAfterRedo}
          value={singleToggleState}
        />
      );

      if (deluxeVar) {
        cartReactElement = (
          <DeluxeToggleArray image_url={image_url}>{toggle}</DeluxeToggleArray>
        );
      } else {
        cartReactElement = toggle;
      }
    } else if (
      autocheck.attachmentStrategy == AttachmentStrategy.DOUBLE_TOGGLE &&
      hasProtectionReturnsAndSplit
    ) {
      let returnToggle: ReactNode = null;
      let packageProtectionToggle: ReactNode = null;

      if (cartEligibleReturns && returnRedoProduct) {
        const price =
          getCorrectlyPricedVariant(
            cartItems,
            returnRedoProduct,
            CartProductType.Return,
          ).price / priceDivisor;
        returnToggle = (
          <RedoToggle
            color={autocheck.textColor}
            coverage={autocheck.coverage}
            customCss={autocheck.customToggleCss}
            fontSize={autocheck.toggleFontSize}
            html={autocheck.html}
            isDelux
            modalCustomizationsV2={autocheck.infoModal}
            modalVersion={autocheck.infoModal?.version}
            onChange={onReturnToggleChange}
            packageProtectionEnabled={
              autocheck.packageProtection.coverageEnabled
            }
            packageProtectionPlusEnabled={
              autocheck.packageProtection.packageProtectionPlusEnabled
            }
            paymentModel={autocheck.paidModel}
            price={new Money(price || +fees.price, cartCurrency)}
            showInfoIcon={autocheck.infoModal?.cartIconEnabled}
            toggleCircleColor={autocheck.cartToggle.toggleCircleColor}
            toggleFields={allToggleOptions.returnToggle}
            toggleOffColor={autocheck.cartToggle.toggleOffColor}
            toggleOnColor={autocheck.cartToggle.toggleOnColor}
            toggleText="returns"
            toggleTextAfterRedo={autocheck.checkboxTextAfterRedo}
            value={returnToggleState}
          />
        );
      }

      if (cartEligibleProtection && packageRedoProduct) {
        const price =
          getCorrectlyPricedVariant(
            cartItems,
            packageRedoProduct,
            CartProductType.PackageProtection,
          ).price / priceDivisor;
        packageProtectionToggle = (
          <RedoToggle
            color={autocheck.textColor}
            coverage={autocheck.coverage}
            customCss={autocheck.customToggleCss}
            fontSize={autocheck.toggleFontSize}
            html={autocheck.html}
            isDelux
            modalCustomizationsV2={autocheck.infoModal}
            modalVersion={autocheck.infoModal?.version}
            onChange={onProtectionToggleChange}
            packageProtectionEnabled={
              autocheck.packageProtection.coverageEnabled
            }
            packageProtectionPlusEnabled={
              autocheck.packageProtection.packageProtectionPlusEnabled
            }
            paymentModel={autocheck.paidModel}
            price={new Money(price || +fees.price, cartCurrency)}
            showInfoIcon={!returnToggle && autocheck.infoModal?.cartIconEnabled}
            toggleCircleColor={autocheck.cartToggle.toggleCircleColor}
            toggleFields={allToggleOptions.packageProtectionToggle}
            toggleOffColor={autocheck.cartToggle.toggleOffColor}
            toggleOnColor={autocheck.cartToggle.toggleOnColor}
            toggleText="Package Protection"
            toggleTextAfterRedo={autocheck.checkboxTextAfterRedo}
            value={protectionToggleState}
          />
        );
      }

      cartReactElement = (
        <DeluxeToggleArray image_url={image_url}>
          {returnToggle}
          {packageProtectionToggle}
        </DeluxeToggleArray>
      );
    } else if (autocheck.attachmentStrategy === AttachmentStrategy.CART_CARD) {
      cartReactElement = (
        <CartCard
          color={autocheck.textColor}
          coverage={autocheck.coverage}
          customCss={autocheck.customToggleCss}
          fontSize={autocheck.toggleFontSize}
          html={autocheck.html}
          icon={autocheck.displayIcon ? autocheck.icon : undefined}
          iconBackgroundColor={iconBackgroundColor}
          iconColor={iconColor}
          image_url={image_url}
          modalCustomizationsV2={autocheck.infoModal}
          modalVersion={autocheck.infoModal?.version}
          onChange={onToggleChange}
          packageProtectionEnabled={autocheck.packageProtection.coverageEnabled}
          packageProtectionPlusEnabled={
            autocheck.packageProtection.packageProtectionPlusEnabled
          }
          paymentModel={autocheck.paidModel}
          price={new Money(singleProductPrice || +fees.price, cartCurrency)}
          showIcons={autocheck.showIcons}
          showInfoIcon={autocheck.infoModal?.cartIconEnabled}
          toggleFields={singleToggleFields}
          value={singleToggleState}
        />
      );
    } else if (
      isAttachmentStrategyUsingCheckoutButtons[autocheck.attachmentStrategy]
    ) {
      cartReactElement = (
        <CheckoutButtons
          customCss={autocheck.customToggleCss}
          defaultCoverageButtonTexts={defaultCoverageButtonTexts}
          defaultNonCoverageButtonText={defaultTextsForNonCoverageButton ?? ""}
          onClickNoCoverage={onClickNoCoverage}
          onCoverageCheckoutClick={onClickCoverage}
        />
      );
    }
    for (const { redoToggleElements, widgetConfig } of toggles) {
      let element = cartReactElement;
      if (
        isAttachmentStrategyUsingCheckoutButtons[
          autocheck.attachmentStrategy
        ] &&
        (widgetConfig?.elementType ===
          WidgetElementType.COVERAGE_CHECKOUT_BUTTON ||
          widgetConfig?.elementType ===
            WidgetElementType.NON_COVERAGE_CHECKOUT_BUTTON)
      ) {
        element = (
          <FlexibleCheckoutButtons
            cartTotal={Number(cart_subtotal)}
            cartTotalPreDiscount={pre_discount_cart_subtotal}
            coveragePrice={Number(singleProductPrice)}
            customCss={autocheck.customToggleCss}
            html={widgetConfig.content}
            isCoverage={
              widgetConfig.elementType ===
              WidgetElementType.COVERAGE_CHECKOUT_BUTTON
            }
            onClickCoverage={onClickCoverage}
            onClickNoCoverage={onClickNoCoverage}
          />
        );
      } else if (
        isAttachmentStrategyUsingCheckoutButtons[
          autocheck.attachmentStrategy
        ] &&
        widgetConfig?.elementType === WidgetElementType.BOTH_CHECKOUT_BUTTONS
      ) {
        const coverageButtonText =
          StorefrontCheckoutTextUtils.getCoverageTextAndSubtext(
            widgetConfig.coverageButtonText,
            textInfo,
          );
        const nonCoverageButtonText =
          StorefrontCheckoutTextUtils.getTextByProduct(
            widgetConfig.nonCoverageButtonText,
            textInfo,
          );
        const coverageButtonStyles = widgetConfig.coverageButtonStyles;

        element = cartReactElement = (
          <CheckoutButtons
            coverageButtonStyles={coverageButtonStyles}
            coverageButtonText={coverageButtonText}
            customCss={autocheck.customToggleCss}
            defaultCoverageButtonTexts={defaultCoverageButtonTexts}
            defaultNonCoverageButtonText={
              defaultTextsForNonCoverageButton ?? ""
            }
            nonCoverageButtonText={nonCoverageButtonText}
            onClickNoCoverage={onClickNoCoverage}
            onCoverageCheckoutClick={onClickCoverage}
          />
        );
      } else if (widgetConfig?.elementType === WidgetElementType.INFO_CARD) {
        const titleAndSubtitle =
          StorefrontCheckoutTextUtils.getCoverageTextAndSubtext(
            widgetConfig.cardText,
            textInfo,
          );

        element = cartReactElement = (
          <InfoCard
            badgeWord={widgetConfig.badgeWord}
            color={autocheck.textColor}
            coverage={autocheck.coverage}
            customCss={autocheck.customToggleCss}
            dataTarget={widgetConfig.dataTarget}
            defaultTexts={defaultTextsForInfoCard}
            fontSize={autocheck.toggleFontSize}
            icon={autocheck.displayIcon ? autocheck.icon : undefined}
            iconBackgroundColor={iconBackgroundColor}
            iconColor={iconColor}
            image_url={image_url}
            imageElement={widgetConfig.imageElement}
            infoCardStyles={widgetConfig.infoCardStyles}
            modalCustomizationsV2={autocheck.infoModal}
            modalSideImage={autocheck.infoModal?.sideImage}
            modalTextAdjustments={autocheck.modalTextAdjustments}
            modalV1Logo={autocheck.modalLogo}
            packageProtectionEnabled={
              autocheck.packageProtection.coverageEnabled
            }
            packageProtectionPlusEnabled={
              autocheck.packageProtection.packageProtectionPlusEnabled
            }
            price={new Money(singleProductPrice || +fees.price, cartCurrency)}
            priceLocation={widgetConfig.priceLocation}
            returnsCoverage={autocheck.coverage}
            showBackground={widgetConfig.showBackground}
            showInfoIcon={
              widgetConfig.showInfoIcon ?? autocheck.infoModal?.cartIconEnabled
            }
            subtitleText={titleAndSubtitle.subtitleText || ""}
            titleText={titleAndSubtitle.titleText || ""}
          />
        );
      }

      cartReactElements.push(
        ...redoToggleElements.map((toggle, i) => (
          <StyleShadowRoot
            key={`${toggle.id}-${cartReactElements.length + i}`}
            shadowRoot={toggle.root}
            styleManager={STYLE_MANAGER}
          >
            <ModalRootContext.Provider value={toggle.modalContainer}>
              <meta found-selector={toggle.getAttribute("found-selector")} />
              {element}
            </ModalRootContext.Provider>
          </StyleShadowRoot>
        )),
      );
    }
  }

  return (
    <>
      {checkbox && showCheckbox ? (
        <StyleShadowRoot
          shadowRoot={checkbox.root}
          styleManager={STYLE_MANAGER}
        >
          <ModalRootContext.Provider value={checkbox.modalContainer}>
            <RedoCheckbox
              checkboxText={autocheck.checkboxText}
              checkboxTextAfterRedo={autocheck.checkboxTextAfterRedo}
              color={autocheck.textColor}
              coverage={autocheck.coverage}
              customCss={autocheck.customPdpCss}
              fontSize={autocheck.checkboxFontSize}
              hideCheckbox={onTapcart ? false : autocheck.hideCheckbox}
              hidePrice={autocheck.hideCheckboxPrice}
              html={autocheck.html}
              infoLink={autocheck.infoIconLink}
              initial={autocheck.redoAutoCheck}
              labelOnly={checkboxLabelOnly}
              modalCustomizationsV2={autocheck.infoModal}
              modalLogo={autocheck.modalLogo}
              modalTextAdjustments={autocheck.modalTextAdjustments}
              modalVersion={autocheck.infoModal?.version}
              onAdd={onModalAdd}
              onChange={onCheckboxChange}
              packageProtectionEnabled={
                autocheck.packageProtection.coverageEnabled
              }
              packageProtectionPlusEnabled={
                autocheck.packageProtection.packageProtectionPlusEnabled
              }
              paymentModel={autocheck.paidModel}
              price={
                !autocheck.packageProtection.coverageEnabled
                  ? new Money(
                      redoPrice || singleProductPrice || +fees.price,
                      cartCurrency,
                    )
                  : undefined
              }
              showInfo={!onTapcart && autocheck.coverageEnabled}
            />
          </ModalRootContext.Provider>
        </StyleShadowRoot>
      ) : null}
      {!(!!onTapcart && isOnProductPage(onTapcart)) &&
        showToggles &&
        cartReactElements}
    </>
  );
});

const getTags = (product: ShopifyProduct) => {
  return product.tags.toLowerCase().split(/, */).filter(Boolean);
};

async function itemValidForReturnsFrontend({
  autocheck,
  item,
  redoShopifyExtensionClient,
  shopifyClient,
  signal,
  invalidReturnProductTags,
  invalidProtectionProductTags,
}: {
  autocheck: WidgetAutocheck;
  item: ShopifyCartItem;
  redoShopifyExtensionClient: ShopifyExtensionClient;
  shopifyClient: ExtensionShopifyClient;
  signal?: AbortSignal;
  invalidReturnProductTags: string[];
  invalidProtectionProductTags?: string[];
}): Promise<{
  returnsValid: boolean;
  protectionValid: boolean;
  finalSaleReturnsValid: boolean;
}> {
  if (location?.host === "checkout.denimtears.com") {
    return {
      returnsValid: true,
      protectionValid: true,
      finalSaleReturnsValid: false,
    };
  }
  if (window.Shopify?.Checkout?.apiHost == "ensorings.myshopify.com") {
    return {
      returnsValid: true,
      protectionValid: true,
      finalSaleReturnsValid: false,
    };
  }

  const returnStorageValue = await getLocalStorageWithExpiry(
    `redo-return-valid-${item.product_id}`,
  );
  const protectionStorageValue = await getLocalStorageWithExpiry(
    `redo-protection-valid-${item.product_id}`,
  );
  const finalSaleStorageValue = await getLocalStorageWithExpiry(
    `redo-final-sale-valid-${item.product_id}`,
  );

  if (
    returnStorageValue !== null &&
    protectionStorageValue !== null &&
    finalSaleStorageValue !== null
  ) {
    return {
      returnsValid: returnStorageValue === "true",
      protectionValid: protectionStorageValue === "true",
      finalSaleReturnsValid: finalSaleStorageValue === "true",
    };
  }

  const product = await shopifyClient.productGet(item.handle, signal);
  const productTags = getTags(product);

  let collections: any[] = [];
  if (
    autocheck.coverageExcludeCollections.length ||
    autocheck.finalSaleReturns.coverageValidCollections.length
  ) {
    if (window.redoStorefront?.type === "bigcommerce") {
      collections =
        (await shopifyClient.getCollections(product.id.toString())) ?? [];
    } else {
      collections =
        (await getCollections(redoShopifyExtensionClient, {
          signal,
          productId: String(item.product_id),
        })) ?? [];
    }
  }

  const properties: string[] = item.properties
    ? Object.keys(item.properties).reduce((acc, key) => {
        const value = item.properties?.[key];
        acc.push(key);
        if (value !== null && value !== "") {
          acc.push(`${key}: ${value}`);
        }
        return acc;
      }, [] as string[])
    : [];

  const hasValidReturnProduct = itemValidForReturns({
    coverageExcludeProperties: autocheck.coverageExcludeProperties,
    coverageExcludeCollections: autocheck.coverageExcludeCollections,
    invalidProductTags: invalidReturnProductTags,
    itemInfo: {
      properties,
      productTags,
      collections: collections.map((c) => c.title),
    },
  });

  const hasValidProtectionProduct = itemValidForProtection({
    invalidProductTags: invalidProtectionProductTags || [],
    itemInfo: {
      properties,
      productTags,
      collections: collections.map((c) => c.title),
    },
  });

  const hasValidFinalSaleProduct = itemValidForFinalSaleReturns({
    validProductTags: autocheck.finalSaleReturns.coverageValidTags,
    validCollections: autocheck.finalSaleReturns.coverageValidCollections,
    itemInfo: { productTags, collections: collections.map((c) => c.handle) },
  });

  setLocalStorageWithExpiry(
    `redo-return-valid-${item.product_id}`,
    String(hasValidReturnProduct),
  );

  setLocalStorageWithExpiry(
    `redo-protection-valid-${item.product_id}`,
    String(hasValidProtectionProduct),
  );

  setLocalStorageWithExpiry(
    `redo-final-sale-valid-${item.product_id}`,
    String(hasValidFinalSaleProduct),
  );

  return {
    returnsValid: hasValidReturnProduct,
    protectionValid: true,
    finalSaleReturnsValid: hasValidFinalSaleProduct,
  };
}

function extractFlatValue(
  input: string,
): { replaceString: string; cartValue: number; storeCredit: number } | null {
  const regex = /%flatvalue(\d+)=(\d+)%/;
  const match = input.match(regex);

  if (match) {
    return {
      replaceString: match[0],
      cartValue: Number(match[1]),
      storeCredit: Number(match[2]),
    };
  }
  return null;
}
